import { applyDateTimeFormatPrefs } from '@shared/locale-format.ts';
import { getCompanyFormatPrefs, getDateLocale } from './locale.ts';

/**
 * Every date and time this app shows a person, rendered in their locale.
 *
 * Dates and times are stored and sent as machine strings — '2026-08-11',
 * '17:00' — because that is what sorts, compares and survives a timezone. None
 * of them is a display string: '17:00' is a fact about a clock, not a way of
 * writing one, and to most of the English-speaking world it reads as an error
 * rather than as five in the afternoon.
 *
 * These live in `i18n/` rather than beside the schedule that first needed them
 * because the schedule is not the only screen with a clock on it. Settings
 * hints, branch overrides, invoice lines and work orders all quote times back
 * to the reader, and a formatter filed under `components/schedule/` is one
 * nobody writing those screens goes looking for — which is exactly how a wire
 * value ends up on screen. Anything user-facing with a date or a time in it
 * imports from here.
 */

/**
 * '09:00' → '9:00 AM' (or '9:00' in locales without an AM/PM convention).
 *
 * Wall-clock only: the string carries no zone, so it is formatted as UTC
 * against a throwaway date and no offset is ever applied. '17:00' means five
 * in the afternoon wherever it was written, and it must still say so on a
 * laptop in another zone.
 */
export function formatLocalTime(timeHHMM: string): string {
  const [hourRaw = '0', minute = '00'] = timeHHMM.split(':');
  const hour = Number(hourRaw);
  // A fixed reference date; only the time-of-day components are read.
  const reference = new Date(Date.UTC(2000, 0, 1, hour, Number(minute)));
  return new Intl.DateTimeFormat(
    getDateLocale(),
    applyDateTimeFormatPrefs({ timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }, getCompanyFormatPrefs()),
  ).format(reference);
}

/**
 * Local calendar dates are formatted at UTC noon so no viewer timezone can
 * shift them across midnight into the wrong day.
 */
function dateAtNoon(isoDate: string): Date {
  return new Date(`${isoDate}T12:00:00Z`);
}

/**
 * Formats an instant, or hands back the string it was given.
 *
 * The fallback is the whole point. `Intl.DateTimeFormat.format` does not
 * degrade on an unparseable value, it throws `RangeError: Invalid time value`
 * — and a throw from a formatter happens during render, where this app has no
 * error boundary to catch it, so one malformed date anywhere turns the entire
 * page white. That is a wildly disproportionate punishment for a wrong string
 * in a caption, and it is exactly what happened when a caller passed a full
 * timestamp to `formatShortDate`. Showing the raw wire value is uglier than
 * showing a date and immeasurably better than showing nothing at all.
 */
function formatOrRaw(
  date: Date,
  raw: string,
  options: Intl.DateTimeFormatOptions,
  locale: string = getDateLocale(),
): string {
  if (Number.isNaN(date.getTime())) return raw;
  const formatted = applyDateTimeFormatPrefs(options, getCompanyFormatPrefs());
  try {
    return new Intl.DateTimeFormat(locale, formatted).format(date);
  } catch (error) {
    // A zone the runtime does not recognise (a legacy, non-IANA value a
    // company kept) throws while the formatter is built. Same reasoning as
    // above: the reader's own zone is a better caption than a white page.
    if (!(error instanceof RangeError) || !formatted.timeZone) throw error;
    const { timeZone: _unknownZone, ...withoutZone } = formatted;
    return new Intl.DateTimeFormat(locale, withoutZone).format(date);
  }
}

/**
 * Formats an instant with the company locale and any date or time override.
 * Callers that build their own `Intl.DateTimeFormat` use this so a saved
 * short/medium/long date or a 12/24-hour clock reaches schedules, invoices
 * and reports, not only the helpers above.
 */
export function formatCompanyInstant(date: Date, options: Intl.DateTimeFormatOptions): string {
  return formatOrRaw(date, '', options);
}

/** '2026-08-11' → 'Tue, Aug 11' (locale-aware; falls back to month/day/year order). */
export function formatShortDate(isoDate: string): string {
  return formatOrRaw(dateAtNoon(isoDate), isoDate, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** '2026-08-11' → 'Tuesday, August 11, 2026' (locale-aware). */
export function formatLongDate(isoDate: string): string {
  return formatOrRaw(dateAtNoon(isoDate), isoDate, {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * '2026-08-11T23:40:00.000Z' → 'Tue, Aug 11' — the day that instant fell on,
 * where the reader is.
 *
 * A separate function from `formatShortDate` because the two take different
 * things and treat the timezone in opposite ways, which is not something a
 * single function can be told apart at runtime. A calendar date is a fact
 * about a calendar: '2026-08-11' is the eleventh everywhere, so it is pinned
 * to UTC and no viewer's clock is allowed to move it. An instant is a moment
 * on a timeline, and the day it belongs to genuinely depends on where you are
 * standing — so it is rendered in the local zone, deliberately.
 *
 * Passing an instant to `formatShortDate` used to build the string
 * '2026-08-11T23:40:00.000ZT12:00:00Z', which is not a date at all. Anything
 * ending in a `Z`, or carrying a `T`, wants this one.
 */
export function formatShortInstant(isoInstant: string): string {
  return formatOrRaw(new Date(isoInstant), isoInstant, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

const NUMERIC_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

/**
 * '2026-10-16' → '10/16/2026' (en-US; '16/10/2026' in es and en-GB).
 *
 * The compact form, for places a weekday would crowd: a dropdown option, a
 * table cell, a caption. A wire date on screen reads as a database leaking
 * through; this is the same fact written the way the reader writes it.
 */
export function formatNumericDate(isoDate: string): string {
  return formatOrRaw(dateAtNoon(isoDate), isoDate, { ...NUMERIC_DATE, timeZone: 'UTC' });
}

/**
 * A visit's stored date and wall-clock time, '2026-10-16' + '09:00' →
 * '10/16/2026 9:00 AM'. Both halves are local to the visit, so neither is
 * shifted by the viewer's zone.
 */
export function formatDateAndTime(isoDate: string, timeHHMM: string | null | undefined): string {
  // A visit still waiting on a time has none; showing it as 12:00 AM would
  // book it for midnight in the reader's eyes.
  if (!timeHHMM || !/^\d{1,2}:\d{2}/.test(timeHHMM)) return formatNumericDate(isoDate);
  return `${formatNumericDate(isoDate)} ${formatLocalTime(timeHHMM)}`;
}

/**
 * '2026-10-16T13:00:00.000Z' → '10/16/2026' — the day that instant fell on,
 * where the reader is (see `formatShortInstant` for why instants are local).
 * Slicing the first ten characters off an instant gives the UTC day instead,
 * which is tomorrow for anything after about 7pm on the US east coast.
 */
export function formatNumericInstant(isoInstant: string): string {
  return formatOrRaw(new Date(isoInstant), isoInstant, NUMERIC_DATE);
}

/**
 * '2026-10-16T13:00:00.000Z' → '10/16/2026, 9:00 AM'. Pass the visit's or
 * location's `timeZone` when there is one, so a visit reads at the hour it
 * happens rather than the hour on the viewer's laptop, and `locale` where the
 * screen follows a language picked in the app.
 */
export function formatInstantDateTime(isoInstant: string, timeZone?: string | null, locale?: string): string {
  return formatOrRaw(
    new Date(isoInstant),
    isoInstant,
    {
      ...NUMERIC_DATE,
      hour: 'numeric',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    },
    locale,
  );
}

/**
 * '2026-10-16T13:00:00.000Z' → '9:00 AM' in `timeZone` (the viewer's when
 * omitted). The hour is never zero-padded: '09:00 AM' is neither the US way of
 * writing it nor the 24-hour one. Pass `locale` where a screen follows a
 * language the person picked in the app rather than the browser's.
 */
export function formatInstantTime(isoInstant: string, timeZone?: string | null, locale?: string): string {
  return formatOrRaw(
    new Date(isoInstant),
    isoInstant,
    {
      hour: 'numeric',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    },
    locale,
  );
}

/**
 * The day a payment was received.
 *
 * A manual payment's `receivedAt` is the calendar day the office picked, stored
 * as midnight UTC (`2026-10-16T00:00:00.000Z`) — a date, not a moment, so it
 * is read as one; localising it would show the day before anywhere west of
 * UTC. Every other `receivedAt` (a card or bank payment's settlement) is a
 * real instant, as is `createdAt`. Same test the money-received report uses.
 */
export function formatPaymentDay(payment: {
  provider?: string | null;
  receivedAt?: string | null;
  createdAt: string;
}): string {
  const { provider, receivedAt, createdAt } = payment;
  if (provider === 'manual' && receivedAt?.endsWith('T00:00:00.000Z')) {
    return formatNumericDate(receivedAt.slice(0, 10));
  }
  return formatNumericInstant(receivedAt ?? createdAt);
}
